import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Vehicle, WidgetType } from '@otw/models'
import {
  DASHBOARD_ACTIVE_QUERY_KEY,
  DASHBOARD_WIDGET_QUERY_ROOT,
  dashboardSoldQueryKey,
  fetchActiveInventoryVehicles,
  fetchSoldVehiclesInRange,
} from 'hooks/inventory/fetchMasterInventory'
import type { DashboardDateRange } from './useDashboardVehicles'
import { dashboardQueryOptions } from './dashboardQueryOptions'

export { useDashboardInventory } from './useDashboardVehicles'

export const dashboardWidgetQueryKey = (
  widgetId: string,
  dateRange?: DashboardDateRange,
  extra?: unknown,
) =>
  [
    DASHBOARD_WIDGET_QUERY_ROOT,
    widgetId,
    dateRange?.start.getTime() ?? null,
    dateRange?.end.getTime() ?? null,
    extra ?? null,
  ] as const


export type DashboardWidgetScope = 'sold' | 'active'

/**
 * Widget-local `select` over sold-in-range when a date range is passed,
 * otherwise active (unsold) inventory. Never the full collection.
 */
export const useDashboardWidgetQuery = <T,>(
  widgetId: WidgetType | string,
  select: (vehicles: Vehicle[], dateRange?: DashboardDateRange) => T,
  dateRange?: DashboardDateRange,
  extra?: unknown,
  scope: DashboardWidgetScope = dateRange ? 'sold' : 'active',
) => {
  const startMs = dateRange?.start.getTime() ?? null
  const endMs = dateRange?.end.getTime() ?? null
  const useSold = scope === 'sold' && startMs !== null && endMs !== null

  const sold = useQuery({
    queryKey: dashboardSoldQueryKey(startMs ?? 0, endMs ?? 0),
    queryFn: () => fetchSoldVehiclesInRange(new Date(startMs ?? 0), new Date(endMs ?? 0)),
    enabled: useSold,
    ...dashboardQueryOptions,
  })
  const inventory = useQuery({
    queryKey: DASHBOARD_ACTIVE_QUERY_KEY,
    queryFn: fetchActiveInventoryVehicles,
    enabled: !useSold,
    ...dashboardQueryOptions,
  })
  const source = useSold ? sold : inventory

  const data = useMemo(() => {
    if (!source.data) return undefined
    return select(source.data, dateRange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.data, startMs, endMs, extra, widgetId, useSold])

  return {
    data,
    isLoading: source.isLoading && !source.data,
    isFetching: source.isFetching,
    isError: source.isError,
    error: source.error,
    refetch: source.refetch,
  }
}
